import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { useCurrentUser } from '../hooks/useCurrentUser';

interface PortalBrand {
  id: number;
  displayName: string;
  snowflakeIdNum: number;
}

interface PortalBrandContextType {
  brands: PortalBrand[];
  isLoadingBrands: boolean;
  selectedBrandId: number | null;
  selectedBrand: PortalBrand | null;
  setSelectedBrandId: (brandId: number) => void;
}

const STORAGE_KEY = 'portal_selected_brand_id';

const PortalBrandContext = createContext<PortalBrandContextType | undefined>(undefined);

export const PortalBrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useCurrentUser();
  const { data: brands = [], isLoading: isLoadingBrands } = trpc.users.myBrands.useQuery(
    undefined,
    {
      enabled: !!user,      // don't query before auth resolves
      staleTime: 60_000,
    },
  );

  const [selectedBrandId, setSelectedBrandIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  });

  // Reconcile stored selection with fresh `brands` list. Fallback to default, then first.
  useEffect(() => {
    if (isLoadingBrands || brands.length === 0) return;
    const stillAllowed = selectedBrandId !== null && brands.some((b) => b.id === selectedBrandId);
    if (stillAllowed) return;

    const defaultId =
      user?.brandId && brands.some((b) => b.id === user.brandId)
        ? user.brandId
        : brands[0].id;

    setSelectedBrandIdState(defaultId);
    localStorage.setItem(STORAGE_KEY, String(defaultId));
  }, [brands, isLoadingBrands, selectedBrandId, user?.brandId]);

  const setSelectedBrandId = useCallback((brandId: number) => {
    setSelectedBrandIdState(brandId);
    localStorage.setItem(STORAGE_KEY, String(brandId));
  }, []);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId) ?? null,
    [brands, selectedBrandId],
  );

  const value = useMemo<PortalBrandContextType>(
    () => ({ brands, isLoadingBrands, selectedBrandId, selectedBrand, setSelectedBrandId }),
    [brands, isLoadingBrands, selectedBrandId, selectedBrand, setSelectedBrandId],
  );

  return <PortalBrandContext.Provider value={value}>{children}</PortalBrandContext.Provider>;
};

export function usePortalBrand(): PortalBrandContextType {
  const ctx = useContext(PortalBrandContext);
  if (!ctx) throw new Error('usePortalBrand must be used within PortalBrandProvider');
  return ctx;
}

export default PortalBrandContext;
